import type { ComponentConfig } from '@puckeditor/core';
import type { JobsiteBusinessContext } from '../../types/runtime-config';
import { PricingInfo } from './pricing-info';
import { PricingInfoFields } from "./pricing-info.fields";
import type { PricingInfoProps } from './pricing-info';
import { RegenerableTextField } from "../editor/RegenerableTextField";
import { StringListField } from "../editor/StringListField";
import { resolveBusinessBoundProp } from "../../lib/business-bound-prop";
import { withBusinessCtaOverride } from "../../lib/business-bound-fields";

const PricingInfoConfig: ComponentConfig<PricingInfoProps> = {
  defaultProps: {
    ctaText: "Get Your Free Quote",
    ctaUrl: "/contact/",
    features: [
      "Diagnostic inspection",
      "Upfront pricing before work begins",
      "Licensed, insured technicians",
      "Warranty on all work",
    ],
    heading: "Transparent, Upfront Pricing",
    priceNote: "Typical service range",
    priceRange: "150-500",
    showFreeEstimate: true,
    subheading: "No hidden fees or surprise charges. Know your costs before we begin.",
  },
  fields: {
    heading: {
      ...PricingInfoFields.heading,
      contentEditable: true,
      render: ({ value, onChange, field }) => (
        <RegenerableTextField
          value={value || ""}
          onChange={onChange}
          namespace="pricing.heading"
          label="Heading"
          multiline={false}
          fieldMeta={field?.metadata}
        />
      ),
    },
    subheading: {
      ...PricingInfoFields.subheading,
      contentEditable: true,
      render: ({ value, onChange, field }) => (
        <RegenerableTextField
          value={value || ""}
          onChange={onChange}
          namespace="pricing.subheading"
          label="Subheading"
          multiline={true}
          fieldMeta={field?.metadata}
        />
      ),
    },
    priceRange: { ...PricingInfoFields.priceRange, contentEditable: true },
    priceNote: { ...PricingInfoFields.priceNote, contentEditable: true },
    ctaText: PricingInfoFields.ctaText,
    ctaUrl: PricingInfoFields.ctaUrl,
    showFreeEstimate: PricingInfoFields.showFreeEstimate,
    features: {
      label: "Included Features",
      render: ({
        value,
        onChange,
      }: {
        value: string[] | undefined;
        onChange: (value: string[]) => void;
      }) => (
        <StringListField
          value={value}
          onChange={onChange}
          label="Included Features"
          placeholder={"Diagnostic inspection\nUpfront pricing before work begins"}
          helpText="One per line."
        />
      ),
      type: "custom",
    },
    backgroundColor: PricingInfoFields.backgroundColor,
  },
  label: "Pricing Info",
  render: ({ id, puck, editMode: _editMode, ...props }) => {
    const business = (puck?.metadata as { business?: JobsiteBusinessContext } | undefined)
        ?.business,
      // PricingInfo is one of the components without a "Call Now"-style default —
      // Its historical default is "Get Your Free Quote". Use the generic helper:
      // Owner-set Site CTA wins over saved AI text, falling back to the component default.
      ctaText = resolveBusinessBoundProp({
        savedValue: props.ctaText,
        resolvedValue:
          business?.ctaPrimaryLabel?.trim() || "Get Your Free Quote",
        hasExplicitBusinessValue: Boolean(business?.ctaPrimaryLabel?.trim()),
      });
    return <PricingInfo id={id} {...props} ctaText={ctaText} />;
  },
  /**
   * The Settings label wins over the saved page value by design; this makes the editor say
   * so instead of offering a box that silently discards what you type.
   */
  resolveFields: (data, { fields, metadata }) => {
    const visible = withBusinessCtaOverride(
      fields as Record<string, unknown>,
      "ctaText",
      metadata,
    );

    // With no price range the component renders its "Contact for Quote" block instead, which
    // Never reads the note.
    if (!(data.props as { priceRange?: string })?.priceRange?.trim()) {
      delete visible.priceNote;
    }

    return visible as typeof fields;
  },
};

export { PricingInfoConfig };
